/* HC custom - Gift quiz ("Find the perfect gift").
   Six questions with a personality feel (who, him or her, their vibe,
   their perfect Saturday, what kind of gift, budget), answered ONLY from
   the store's holiday collections: the ones picked in the section's
   collection list, or when that is empty the ones linked on the page. Each
   collection loads through the theme's hc-gift-quiz JSON view in its own
   merchandised order with sold-out products filtered; an unpublished or
   empty one is skipped on its own and shows up the day it is published.

   Collections are read by name: gift cards, stocking stuffers, price
   tiers, men's / women's / kids' gift collections, curated picks, and
   themes. Names also say who a collection is for (Mom, Dad, Grand, It
   Girl, Kids) and which vibe it fits (trail and outdoor = adventurer, cozy
   = homebody, statement and style = trendsetter, games and toys =
   game-night host, fitness and running = athlete, staff picks and "has
   everything" = has everything). Product types and title words back the
   collections up, so every vibe works even before its collection exists.

   Results open with their gift personality ("She's a cozy homebody with
   an adventurous side"), then a top 3, rows of more ideas, stocking
   stuffers and the gift card. Cards are built from the collection data;
   options load only when a shopper taps Add (one size: straight to the
   cart; more: a size and colour picker on the card), and the add goes
   through the theme's own CartLinesUpdateEvent.

   The answers live in the URL (?gift=...) so a result set is a link. Adds
   carry properties[_hc_gift] = "<who>-<collection>" so order exports show
   which answers sell. No ML recommendations, ever. */

import { CartLinesUpdateEvent } from '@shopify/events';

const money = (n) => `$${Number(n).toFixed(2).replace(/\.00$/, '')}`;
const padded = (t) => ` ${String(t || '').toUpperCase()} `;
const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const isColourName = (n) => /colou?r/i.test(n || '');
const isSizeName = (n) => /size/i.test(n || '');
const lastOf = (full) => String(full || '').split(' - ').pop().trim();
const ROOT = window.Shopify?.routes?.root || '/';
/* Image URLs from the JSON view and the product JSON, at the size a card needs. */
const sized = (url, w) => {
  if (!url) return '';
  const u = String(url);
  return /[?&]width=\d+/.test(u) ? u.replace(/([?&])width=\d+/, `$1width=${w}`) : `${u}${u.includes('?') ? '&' : '?'}width=${w}`;
};

/* --- the questions ---------------------------------------------------- */
/* Who it is for. `aud` is implied (Mom is her); `ask` asks him or her. */
const WHO = [
  { v: 'partner', label: 'Partner', hint: 'Someone special', ask: true },
  { v: 'mom', label: 'Mom', hint: 'Or a mom figure', aud: 'w' },
  { v: 'dad', label: 'Dad', hint: 'Or a dad figure', aud: 'm' },
  { v: 'grand', label: 'Grandparent', hint: 'Grandma or grandpa', ask: true },
  { v: 'teen', label: 'Teen', hint: 'Thirteen to eighteen', ask: true },
  { v: 'kid', label: 'Kid', hint: 'Toys, games and more', aud: 'k' },
  { v: 'friend', label: 'Friend', hint: 'The group chat', ask: true },
  { v: 'cowork', label: 'Coworker', hint: 'Or a Secret Santa', ask: true },
];
const GENDER = [
  { v: 'm', label: 'Him', hint: 'Shop his picks' },
  { v: 'w', label: 'Her', hint: 'Shop her picks' },
  { v: 'a', label: 'Either', hint: 'Not sure, or it doesn’t matter' },
];
/* The vibes. `cols` reads collection names, `types` product types,
   `words` product titles. {they} {them} {their} resolve per person. */
const VIBES = [
  {
    v: 'adventurer', label: 'The adventurer', hint: 'Always planning the next trip', name: 'an adventurer', side: 'adventurous side',
    line: 'Happiest outside, with gear that keeps up.',
    cols: /\b(trail|outdoors?|adventures?|hike|hiking|camp|camping|explore)\b/,
    types: / (CAMP|HIKE|PACK|BACKPACK|LIGHTING|KNIVES|COOLERS|WATER BOTTLES|JACKETS RAIN|JACKETS INSULATED|VEST INSULATED|BOOTS HIKE|SHOES HIKE|GEAR REPAIR)/,
    words: /\b(trail|hike|hiking|camp|summit|alpine|headlamp|binoculars?|monocular)\b/i,
  },
  {
    v: 'homebody', label: 'The homebody', hint: 'Cozy nights in, every night', name: 'a cozy homebody', side: 'cozy side',
    line: 'Soft layers, good candles, nowhere to be.',
    cols: /\b(cozy|comforts?|lounge|home)\b/,
    types: / (HOODIES|SWEATERS|LOUNGE|SLIPPERS|CANDLES|CUPS\|MUGS|BLANKETS|SOCKS)/,
    words: /\b(cozy|fleece|sherpa|slippers?|candle|blanket|plush|lounge|fuzzy)\b/i,
  },
  {
    v: 'trendsetter', label: 'The trendsetter', hint: 'Knows what’s next first', name: 'a trendsetter', side: 'stylish side',
    line: 'Always a step ahead, and it shows.',
    cols: /\b(statement|it girl|style|stylish|trend|trendsetter|fashion|chic)\b/,
    types: / (JEWELRY|HAIR ACCESSORIES|PURSE|SUNGLASSES|DRESS|SCARVES|JACKETS CASUAL|SHOES CASUAL|BOOTS CASUAL)/,
    words: /\b(statement|leather|suede|waxed|chain|gold|velvet)\b/i,
  },
  {
    v: 'gamenight', label: 'The game-night host', hint: 'Snacks out, games on', name: 'the game-night host', side: 'competitive side',
    line: 'Never met a game {they} didn’t want to win.',
    cols: /\b(game|games|toys?|play)\b/,
    types: / (GAMES|HEADPHONES\|SPEAKER|COOLERS SOFT|DRINK ACCESS)/,
    words: /\b(game|toss|darts|cards|trivia|hockey|frisbee|cornhole|speaker)\b/i,
  },
  {
    v: 'athlete', label: 'The athlete', hint: 'Up early, always training', name: 'an athlete', side: 'athletic side',
    line: 'Up early, moving often, always training for something.',
    cols: /\b(fitness|athletes?|active|run|runners?|running|gym|sports?)\b/,
    types: / (ACTIVE|RUNNING|LEGGINGS|HEADPHONES|WATER BOTTLES)/,
    words: /\b(run|running|train|training|gym|yoga|sport|performance)\b/i,
  },
  {
    v: 'everything', label: 'Has everything', hint: 'Impossible to shop for', name: 'the one who has everything', side: '',
    line: 'Hard to shop for, so we went with the best of the best.',
    cols: /\b(everything|staff|top|best|splurge|picks?)\b/,
    types: null,
    words: null,
  },
];
const VIBE = Object.fromEntries(VIBES.map((x) => [x.v, x]));
const SATURDAY = [
  { v: 'adventurer', label: 'A trail at sunrise', icon: 'sat-trail' },
  { v: 'homebody', label: 'The couch and a movie', icon: 'sat-couch' },
  { v: 'trendsetter', label: 'Brunch and shopping', icon: 'sat-brunch' },
  { v: 'gamenight', label: 'Game day with friends', icon: 'sat-game' },
  { v: 'athlete', label: 'An early run', icon: 'sat-run' },
];
const KINDS = [
  { v: 'wear', label: 'Something to wear', hint: 'Layers, shoes, hats', icon: 'kind-wear' },
  { v: 'use', label: 'Something to use', hint: 'Gear, drinkware, games', icon: 'kind-use' },
  { v: 'extras', label: 'Little extras', hint: 'Stocking stuffers', icon: 'kind-extras' },
  { v: 'any', label: 'Surprise me', hint: 'A bit of everything', icon: 'surprise' },
];
const STEP_LABELS = { who: 'Who', gender: 'Him or her', vibe: 'Vibe', saturday: 'Saturday', kind: 'Gift', budget: 'Budget' };

/* A vibe is offered when this many of the person's gifts fit it (kids
   have a smaller shelf); a gift kind and a budget band when this many fit
   the answers. */
const MIN_VIBE = 6;
const MIN_VIBE_KIDS = 3;
const MIN_FIT = 4;
/* The categories every set could hold (shades, caps, bottles, mugs): in the
   stocking-stuffer row they give a little ground, or the same pair of
   sunglasses ends up in every shopper's set. */
const UNIVERSAL = new Set(['SUNGLASSES', 'HATS BILLED', 'WATER BOTTLES', 'CUPS|MUGS', 'DRINK ACCESS', 'TOWELS', 'SUNGLASS ACCESS']);

/* Line icons (24-box, currentColor strokes). A photo set in the section's
   "Answer photo" blocks replaces a Who icon. */
const ICON = {
  'who-partner': '<path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z"/>',
  'who-mom': '<path d="M6.5 13C6 9 8 4.5 12 4.5s6 4.5 5.5 8.5"/><circle cx="12" cy="9" r="3.2"/><path d="M4.5 20c.5-4 3.7-6 7.5-6s7 2 7.5 6"/>',
  'who-dad': '<circle cx="12" cy="8" r="4"/><path d="M4.5 20c.5-4.2 3.7-6.5 7.5-6.5s7 2.3 7.5 6.5"/>',
  'who-grand': '<circle cx="12" cy="8" r="4"/><path d="M9 8h6M4.5 20c.5-4.2 3.7-6.5 7.5-6.5s7 2.3 7.5 6.5"/><path d="M19 13v7"/>',
  'who-teen': '<rect x="7" y="3" width="10" height="18" rx="2"/><path d="M11 18h2"/>',
  'who-kid': '<path d="M12 3l6 8-6 9-6-9z"/><path d="M6 11h12M12 3v17"/><path d="M12 20c-1.5 1-3 1-3.5 2.5"/>',
  'who-friend': '<circle cx="8.5" cy="9" r="3"/><circle cx="15.5" cy="9" r="3"/><path d="M3 20c.4-3.3 2.6-5 5.5-5s5.1 1.7 5.5 5M10 20c.4-3.3 2.6-5 5.5-5s5.1 1.7 5.5 5"/>',
  'who-cowork': '<rect x="3" y="8" width="18" height="12" rx="2"/><path d="M9 8V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M3 13h18"/>',
  'sat-trail': '<path d="M2 19l7-11 4 6 3-4 6 9z"/><circle cx="17" cy="5" r="2"/>',
  'sat-couch': '<path d="M4 11V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3"/><path d="M3 11a2 2 0 0 1 2 2v2h14v-2a2 2 0 1 1 2 2v3H3v-3a2 2 0 0 1 0-4z"/><path d="M6 18v2M18 18v2"/>',
  'sat-brunch': '<path d="M5 9h11v5a5 5 0 0 1-5 5h-1a5 5 0 0 1-5-5z"/><path d="M16 10h1.5a2.5 2.5 0 0 1 0 5H16"/><path d="M9 3c0 1.5-1 1.5-1 3M12 3c0 1.5-1 1.5-1 3"/>',
  'sat-game': '<path d="M8 21h8M12 17v4"/><path d="M7 4h10v4a5 5 0 0 1-10 0z"/><path d="M7 6H4a3 3 0 0 0 3 4M17 6h3a3 3 0 0 1-3 4"/>',
  'sat-run': '<path d="M3 17h17.5a1.5 1.5 0 0 0 1.5-1.5c0-1.4-.9-2.4-2.3-2.9L14 10.5 12 7H9l-1 3.5-4 1.2L3 17z"/><path d="M3 17v2h18.5"/>',
  'kind-wear': '<path d="M8 4l4 2 4-2 5 3-2 4-2-1v11H7V10l-2 1-2-4z"/>',
  'kind-use': '<rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12h18"/>',
  'kind-extras': '<rect x="4" y="9" width="16" height="11" rx="1.5"/><path d="M12 9v11M4 13h16"/><path d="M12 9c-2.2 0-4.4-.8-4.4-2.5a1.7 1.7 0 0 1 3.3-.5C11.4 7.2 12 9 12 9zm0 0c2.2 0 4.4-.8 4.4-2.5a1.7 1.7 0 0 0-3.3-.5C12.6 7.2 12 9 12 9z"/>',
  surprise: '<path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z"/><path d="M19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8z"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  check: '<path d="M5 12.5l4.5 4.5L19 7.5"/>',
  close: '<path d="M6 6l12 12M18 6L6 18"/>',
};
const icon = (key, cls = 'hc-gf__icon') =>
  `<svg class="${cls}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICON[key] || ICON.surprise}</svg>`;

/* --- tiny helpers ---------------------------------------------------- */
function hash(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/* Who a product is for, read the way the store files it: the type's
   department first (matched with spaces, so MENS never matches inside
   WOMENS), then the title ("women's" never trips the men test: there is no
   word boundary before the "men" inside it), then the Men / Women / Kids
   tags. 'u' = for anyone. */
function deptOf(p) {
  const T = padded(p.ty);
  if (T.includes(' KIDS ') || T.includes(' BABY ')) return 'k';
  if (T.includes(' WOMENS ')) return 'w';
  if (T.includes(' MENS ')) return 'm';
  const t = ` ${String(p.t || '').toLowerCase().replace(/’/g, "'")} `;
  if (/\b(women'?s|womens|ladies)\b/.test(t)) return 'w';
  if (/\b(men'?s|mens)\b/.test(t)) return 'm';
  if (/\b(kids?'?|youth|boys?'?|girls?'?|toddler|baby|infant)\b/.test(t)) return 'k';
  const has = (x) => p.tags.includes(x);
  if ((has('Kids') || has('Baby') || has('Boys') || has('Girls')) && !has('Men') && !has('Women')) return 'k';
  if (has('Women') && !has('Men')) return 'w';
  if (has('Men') && !has('Women')) return 'm';
  return 'u';
}

/* Something to wear: apparel, footwear, socks, eyewear (01-07), hats (11),
   and the worn accessories; everything else is something to use. */
const isWear = (ty) => /^0[1-7]\.|^11\./.test(String(ty || '').trim()) || / (BELTS|GLOVES|HAIR ACCESSORIES|JEWELRY|SCARVES|WATCHES)\b/.test(padded(ty));

/* Colourways and lengths of one product are separate products in this
   store ("Kore Short - 7 in.", "Kore Short - 5 in."); the root keeps a set
   from showing the same thing twice. */
function rootOf(title) {
  return String(title || '')
    .toLowerCase()
    .replace(/^(men's|mens|women's|womens|w|m|kids'?|youth|boys'?|girls'?|toddler|baby)\s+/, '')
    .split(/\s[-–|]\s/)[0]
    .replace(/\b\d+(\.\d+)?\s?(oz|l|ml|in\.?|inch|mm|liter|litre|pack)\b/g, '')
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/* What a collection is, from its handle and title. Audience collections
   are matched strictly (mens-gifts, gifts-for-her...) so "For the Dad Who
   Has Everything" stays a theme. */
function kindOf(c, products) {
  const h = c.handle;
  const s = `${h} ${String(c.title || '').toLowerCase()}`.replace(/-/g, ' ');
  if (/\bgift ?cards?\b/.test(s) || (products.length && products.every((p) => /gift ?card/i.test(p.ty || '')))) return 'giftcard';
  if (/\bstocking\b/.test(s)) return 'stuffers';
  if (/\b(under|below|less than)\s*\$?\d|\$?\d+\s*(and|&)\s*under\b/.test(s)) return 'price';
  if (/^(mens?|men-s|guys?|him|his)-gifts?$|^gifts?-for-(him|men|guys)$/.test(h)) return 'men';
  if (/^(womens?|women-s|ladies|her|hers)-gifts?$|^gifts?-for-(her|women|ladies)$/.test(h)) return 'women';
  if (/^(kids?|childrens?)-gifts?$|^gifts?-for-(kids|children)$/.test(h)) return 'kids';
  if (/(^|-)(top|best|staff|picks?|favou?rites?|editors?)(-|$)/.test(h)) return 'curated';
  return 'theme';
}
/* A theme whose name is for someone ("Cozy Comforts for Mom", "It Girl
   Essentials", "Cozy Gifts for Him") is offered to that person only. */
function themeAudience(title) {
  const t = ` ${String(title || '').toLowerCase().replace(/['’]/g, '')} `;
  if (/\b(mom|moms|mother|mothers|her|hers|women|womens|ladies|girl|girls|wife|grandma|sister)\b/.test(t)) return 'w';
  if (/\b(dad|dads|father|fathers|him|his|men|mens|guy|guys|husband|grandpa|brother|man)\b/.test(t)) return 'm';
  return '';
}
/* Who a collection is made for, when its name says so. */
function relOf(text, kind) {
  if (kind === 'kids' || /\b(kids?|toys?)\b/.test(text)) return 'kid';
  if (/\b(moms?|mothers?)\b/.test(text)) return 'mom';
  if (/\b(dads?|fathers?)\b/.test(text)) return 'dad';
  if (/\bgrand(ma|pa|parents?|mothers?|fathers?)?\b/.test(text)) return 'grand';
  if (/\b(teens?|it girl)\b/.test(text)) return 'teen';
  return '';
}

/* --- the element ------------------------------------------------------ */
class HcGiftFinder extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('[data-hc-gf-stage]');
    this.config = this.readConfig();
    if (!this.stage || !this.config) return;
    this.sources = this.discover();
    if (!this.sources.length) {
      /* Nothing to choose from: the merchant sees why, shoppers see nothing. */
      if (window.Shopify?.designMode) {
        this.stage.innerHTML = `<p class="hc-gf__empty">Pick the gift collections in this section's settings (Gift collections), or add collection links to this page.</p>`;
      } else {
        this.hidden = true;
      }
      return;
    }
    this.state = this.blank();
    this.at = 'who';
    this.cards = new Map();
    this.qid = `hc-gf-q-${this.getAttribute('data-section-id') || 'x'}`;
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.addEventListener('keydown', this.onKey);
    this.addEventListener('submit', this.onSubmit);
    const fromUrl = new URLSearchParams(location.search).get('gift');
    if (fromUrl) {
      this.renderSkeleton();
      this.load().then((ok) => {
        const s = ok && this.decode(fromUrl);
        if (s) {
          this.state = s;
          this.showResults();
        } else {
          this.renderStep();
        }
      });
    } else {
      this.renderStep();
      this.watch();
    }
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }

  blank() {
    return { who: '', gender: '', vibe: '', sat: '', kind: '', band: null, deal: 0 };
  }

  readConfig() {
    try {
      const cfg = JSON.parse(this.querySelector('[data-hc-gf-config]').textContent);
      if (!cfg || !cfg.kb) return null;
      cfg.bands = (cfg.bands || [50, 100, 200]).map(Number);
      cfg.rail = Number(cfg.rail) || 8;
      cfg.stufferMax = Number(cfg.stufferMax) || 35;
      cfg.labels = cfg.labels || {};
      cfg.season = String(cfg.season || '').trim().toLowerCase();
      cfg.collections = Array.isArray(cfg.collections) ? cfg.collections : [];
      return cfg;
    } catch (e) {
      return null;
    }
  }

  /* --- the catalogue ---------------------------------------------------- */
  /* The section's collection list wins when set. Otherwise every
     collection linked inside <main> (tiles, lists, "view all" links), plus
     the collection tabs, which carry their handle on the panel. */
  discover() {
    const out = new Map();
    const add = (handle, label) => {
      const h = String(handle || '').trim().toLowerCase();
      if (!h || h === 'all' || h === 'vendors' || h === 'types') return;
      const had = out.get(h);
      if (!had) out.set(h, { handle: h, label: label || '' });
      else if (label && !had.label) had.label = label;
    };
    if (this.config.collections.length) {
      for (const c of this.config.collections) add(c.h, c.t);
      return [...out.values()];
    }
    const main = document.querySelector('main') || document.body;
    const prefix = ROOT.replace(/\/$/, '');
    for (const el of main.querySelectorAll('a[href], [data-collection-handle]')) {
      if (this.contains(el)) continue;
      if (el.hasAttribute('data-collection-handle')) {
        add(el.getAttribute('data-collection-handle'), el.getAttribute('data-tab-label') || '');
        continue;
      }
      let url;
      try {
        url = new URL(el.getAttribute('href'), location.href);
      } catch (e) {
        continue;
      }
      if (url.origin !== location.origin) continue;
      let path = url.pathname;
      if (prefix && path.startsWith(`${prefix}/`)) path = path.slice(prefix.length);
      const m = path.match(/^\/collections\/([^/]+)\/?$/);
      if (m) add(decodeURIComponent(m[1]));
    }
    return [...out.values()];
  }

  /* Start loading when the quiz is near the screen or touched, so the
     collections are in hand by the vibe question. */
  watch() {
    const go = () => {
      this.io?.disconnect();
      this.load();
    };
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && go(), { rootMargin: '600px 0px' });
      this.io.observe(this);
    } else {
      go();
    }
    this.addEventListener('pointerdown', go, { once: true });
    this.addEventListener('focusin', go, { once: true });
  }

  load() {
    if (this.ready) return this.ready;
    this.ready = (async () => {
      const list = this.sources;
      const results = new Array(list.length).fill(null);
      let next = 0;
      const worker = async () => {
        while (next < list.length) {
          const i = next++;
          results[i] = await HcGiftFinder.collection(list[i].handle);
        }
      };
      await Promise.all(Array.from({ length: Math.min(4, list.length) }, worker));
      if (results.every((r) => r === null)) throw new Error('offline');
      this.build(results);
      return true;
    })().catch(() => {
      this.ready = null;
      return false;
    });
    return this.ready;
  }

  build(results) {
    const ex = this.config.kb.exclude || {};
    this.pool = new Map();
    this.cols = [];
    this.giftCard = null;
    results.forEach((data, i) => {
      const src = this.sources[i];
      if (!data || !Array.isArray(data.products) || !data.products.length) return;
      const col = { handle: data.handle || src.handle, title: data.title || src.label || src.handle, image: data.image || '', items: [] };
      col.label = src.label || col.title;
      col.kind = kindOf(col, data.products);
      const text = `${col.title} ${col.handle.replace(/-/g, ' ')}`.toLowerCase();
      col.aud = col.kind === 'theme' ? themeAudience(col.title) : '';
      col.rel = relOf(text, col.kind);
      col.kid = col.rel === 'kid' || (col.kind === 'theme' && /\b(games?|play)\b/.test(text));
      col.vibes = VIBES.filter((x) => x.cols.test(text)).map((x) => x.v);
      if (col.kind === 'giftcard') {
        if (!this.giftCard) {
          const card = data.products.find((p) => /digital|online|e-?gift/i.test(`${p.h} ${p.t}`)) || data.products[0];
          this.giftCard = { label: col.label, img: card.img || col.image, url: `${ROOT}collections/${col.handle}` };
        }
        return;
      }
      data.products.forEach((p, pos) => {
        if (!p || !p.h || !p.img) return;
        const tags = Array.isArray(p.tags) ? p.tags : [];
        if ((ex.tags || []).some((t) => tags.includes(t))) return;
        if ((ex.types || []).some((t) => String(p.ty || '').includes(t))) return;
        if ((ex.handles || []).includes(p.h)) return;
        let item = this.pool.get(p.h);
        if (!item) {
          item = { h: p.h, t: p.t || '', ty: p.ty || '', last: lastOf(p.ty), v: p.v || '', p: Number(p.p) || 0, pm: Number(p.pm) || Number(p.p) || 0, cp: Number(p.cp) || 0, img: p.img, tags, in: new Map() };
          item.dept = deptOf(item);
          item.root = rootOf(item.t);
          item.pet = / PET /.test(padded(item.ty));
          item.wear = isWear(item.ty);
          item.staff = tags.some((t) => /^(_badge_staff_pick|staffholiday)$/i.test(t));
          this.pool.set(p.h, item);
        }
        if (!item.in.has(col.handle)) {
          item.in.set(col.handle, pos);
          col.items.push(item);
        }
      });
      if (col.items.length) this.cols.push(col);
    });
    this.byHandle = new Map(this.cols.map((c) => [c.handle, c]));
    this.stuffHandles = this.cols.filter((c) => c.kind === 'stuffers').map((c) => c.handle);
    /* Which vibes each product fits: 2 = its collection says so, 1 = its type or title does. */
    for (const it of this.pool.values()) {
      it.vibes = {};
      const T = padded(it.ty);
      for (const x of VIBES) {
        let hit = 0;
        for (const h of it.in.keys()) if (this.byHandle.get(h)?.vibes.includes(x.v)) hit = 2;
        if (!hit && ((x.types && x.types.test(T)) || (x.words && x.words.test(it.t)))) hit = 1;
        if (x.v === 'everything' && !hit && it.staff) hit = 2;
        if (hit) it.vibes[x.v] = hit;
      }
    }
  }

  inKind(item, test) {
    for (const h of item.in.keys()) {
      const c = this.byHandle.get(h);
      if (c && test(c)) return true;
    }
    return false;
  }

  /* --- who the gift is for ------------------------------------------- */
  aud(s = this.state) {
    const w = WHO.find((x) => x.v === s.who);
    return w?.aud || s.gender || '';
  }
  /* Kids get the kids' department plus anything unisex in a kids' theme;
     "either" gets unisex only; dog toys never (the quiz never asks about a
     dog). */
  eligible(item, aud) {
    if (item.pet) return false;
    if (aud === 'k') return item.dept === 'k' || (item.dept === 'u' && this.inKind(item, (c) => c.kid));
    if (aud === 'a') return item.dept === 'u';
    return item.dept === aud || item.dept === 'u';
  }
  people(s = this.state) {
    const a = this.aud(s);
    if (a === 'm') return { is: 'He’s', obj: 'him', pos: 'his', they: 'he' };
    if (a === 'w') return { is: 'She’s', obj: 'her', pos: 'her', they: 'she' };
    return { is: 'They’re', obj: 'them', pos: 'their', they: 'they' };
  }
  /* The collections made for this person (Mom, Dad, Grand, It Girl, Kids). */
  relFit(item, s) {
    const rel = s.who === 'teen' ? (this.aud(s) === 'w' ? 'teen' : '') : s.who;
    if (!rel) return false;
    return this.inKind(item, (c) => c.rel === rel);
  }
  /* The person's whole shelf, before any answer narrows it. */
  shelf(s) {
    const a = this.aud(s);
    return [...this.pool.values()].filter((it) => this.eligible(it, a));
  }

  vibeOptions(s) {
    if (!this.cols) return [];
    const shelf = this.shelf(s);
    const min = this.aud(s) === 'k' ? MIN_VIBE_KIDS : MIN_VIBE;
    const out = [];
    const used = new Set();
    for (const x of VIBES) {
      const fits = shelf.filter((it) => it.vibes[x.v]);
      if (fits.length < min) continue;
      /* The tile shows the vibe's best product for them (its collection's
         first), never one another tile already shows, nor its brand (two
         Barbour wax jackets read as the same photo). */
      const ranked = fits.slice().sort((a, b) => b.vibes[x.v] - a.vibes[x.v] || Math.min(...a.in.values()) - Math.min(...b.in.values()));
      const best = ranked.find((it) => !used.has(it.root) && !used.has(`v:${it.v}`)) || ranked.find((it) => !used.has(it.root)) || ranked[0];
      used.add(best.root);
      used.add(`v:${best.v}`);
      out.push({ v: x.v, label: x.label, hint: x.hint, img: best.img });
    }
    return out;
  }

  /* The kind and budget filters. */
  kindFit(it, kind) {
    if (kind === 'wear') return it.wear;
    if (kind === 'use') return !it.wear;
    if (kind === 'extras') return it.p <= this.config.stufferMax;
    return true;
  }
  kindOptions(s) {
    const shelf = this.shelf(s);
    return KINDS.filter((k) => k.v === 'any' || shelf.filter((it) => this.kindFit(it, k.v)).length >= MIN_FIT).map((k) =>
      k.v === 'extras' ? { ...k, hint: `Stocking stuffers under ${money(this.config.stufferMax)}` } : k
    );
  }
  bands() {
    const [a, b, c] = this.config.bands;
    return [
      { v: 1, lo: 0, hi: a, figure: `Under ${money(a)}`, label: 'A thoughtful small gift' },
      { v: 2, lo: a, hi: b, figure: `${money(a)}–${money(b)}`, label: 'The sweet spot' },
      { v: 3, lo: b, hi: c, figure: `${money(b)}–${money(c)}`, label: 'Something special' },
      { v: 4, lo: c, hi: Infinity, figure: `${money(c)}+`, label: 'Go big' },
    ];
  }
  bandLabel(v) {
    if (!v) return 'Any budget';
    const [a, b, c] = this.config.bands;
    return [`Under ${money(a)}`, `${money(a)} to ${money(b)}`, `${money(b)} to ${money(c)}`, `${money(c)} and up`][v - 1] || '';
  }
  bandOf(s) {
    if (s.kind === 'extras') return { lo: 0, hi: this.config.stufferMax + 0.001 };
    const b = this.bands().find((x) => x.v === s.band);
    return b ? { lo: b.lo, hi: b.hi } : { lo: 0, hi: Infinity };
  }
  bandOptions(s) {
    const list = this.shelf(s).filter((it) => this.kindFit(it, s.kind));
    const opts = this.bands().filter((b) => list.filter((it) => it.p >= b.lo && it.p < b.hi).length >= MIN_FIT);
    return [...opts, { v: 0, figure: 'Any budget', label: 'Show me the best' }];
  }

  path(s = this.state) {
    const w = WHO.find((x) => x.v === s.who);
    const steps = ['who'];
    if (!w || w.ask) steps.push('gender');
    steps.push('vibe', 'saturday', 'kind');
    if (s.kind !== 'extras') steps.push('budget');
    return steps;
  }

  /* --- URL state ------------------------------------------------------- */
  /* who.gender.vibe.saturday.kind.band.deal: "mom..homebody.adventurer.wear.2.0".
     None of the values holds a dot, and dots are not escaped in a query string. */
  encode(s) {
    return [s.who, s.gender || '', s.vibe, s.sat, s.kind, s.band ?? 0, s.deal || 0].join('.');
  }
  decode(str) {
    const [who, gender, vibe, sat, kind, band, deal] = String(str || '').split('.');
    const w = WHO.find((x) => x.v === who);
    if (!w) return null;
    const s = { who, gender: w.ask ? (GENDER.some((g) => g.v === gender) ? gender : 'a') : '', vibe: '', sat: '', kind: '', band: 0, deal: Math.max(0, Math.min(99, parseInt(deal, 10) || 0)) };
    if (!this.vibeOptions(s).some((o) => o.v === vibe)) return null;
    s.vibe = vibe;
    s.sat = SATURDAY.some((x) => x.v === sat) ? sat : '';
    s.kind = KINDS.some((k) => k.v === kind) ? kind : 'any';
    s.band = Math.max(0, Math.min(4, parseInt(band, 10) || 0));
    return s;
  }
  syncUrl(clear) {
    const url = new URL(location.href);
    if (clear) url.searchParams.delete('gift');
    else url.searchParams.set('gift', this.encode(this.state));
    history.replaceState(history.state, '', url);
  }

  track(name, data) {
    try {
      window.Shopify?.analytics?.publish?.(`hc_gift_quiz_${name}`, data || {});
    } catch (e) {
      /* analytics are optional */
    }
  }

  /* --- survey ---------------------------------------------------------- */
  renderStep() {
    const L = this.config.labels;
    const s = this.state;
    const qid = this.qid;
    const path = this.path();
    const key = this.at;
    const index = Math.max(0, path.indexOf(key));
    const needsData = ['vibe', 'kind', 'budget'].includes(key);

    if (needsData && !this.cols) {
      this.renderWaiting(key, path, index);
      this.load().then((ok) => {
        if (this.at !== key) return;
        if (!ok) return this.renderError();
        this.renderStep();
      });
      return;
    }

    /* Answer tiles: the input stays in the tree for keyboard and screen
       readers; the sibling span is the visible tile. "grid" = two columns
       (four for Who on wider screens), "list" = a photo beside the text. An
       odd count in two columns ends on one full-width tile. */
    const tiles = (name, items, type, checked, variant) => {
      const wide = variant !== 'quad' && items.length % 2 === 1;
      return `<div class="hc-gf__tiles hc-gf__tiles--${variant}" role="${type === 'radio' ? 'radiogroup' : 'group'}" aria-labelledby="${qid}">${items
        .map((it, i) => {
          let art;
          if (it.img) art = `<span class="hc-gf__tile-art hc-gf__tile-art--photo"><img src="${esc(sized(it.img, 240))}" alt="" loading="lazy" decoding="async" width="120" height="120"></span>`;
          else if (it.figure) art = `<span class="hc-gf__tile-art hc-gf__tile-art--figure">${esc(it.figure)}</span>`;
          else art = `<span class="hc-gf__tile-art">${icon(it.icon)}</span>`;
          const cls = ['hc-gf__tile', wide && i === items.length - 1 ? 'hc-gf__tile--wide' : '', it.img ? 'hc-gf__tile--photo' : ''].filter(Boolean).join(' ');
          return `<label class="${cls}">
            <input type="${type}" name="${name}" value="${esc(it.v)}" ${checked(it.v) ? 'checked' : ''}>
            <span class="hc-gf__tile-face">${art}<span class="hc-gf__tile-text"><span class="hc-gf__tile-label">${esc(it.label)}</span>${it.hint ? `<span class="hc-gf__tile-hint">${esc(it.hint)}</span>` : ''}</span></span>
          </label>`;
        })
        .join('')}</div>`;
    };

    const pp = this.people();
    let question = '';
    let hint = '';
    let body = '';
    if (key === 'who') {
      const images = this.config.images || {};
      question = L.questionAudience;
      hint = 'Pick one.';
      body = tiles('who', WHO.map((w) => ({ ...w, img: images[`who-${w.v}`] || '', icon: `who-${w.v}` })), 'radio', (v) => s.who === v, 'quad');
    } else if (key === 'gender') {
      question = L.questionGender;
      hint = 'So the picks fit.';
      body = tiles('gender', GENDER, 'radio', (v) => s.gender === v, 'grid');
    } else if (key === 'vibe') {
      const opts = this.vibeOptions(s);
      if (s.vibe && !opts.some((o) => o.v === s.vibe)) s.vibe = '';
      question = L.questionVibe;
      hint = `Pick the one that sounds most like ${pp.obj}.`;
      body = tiles('vibe', opts, 'radio', (v) => s.vibe === v, 'list');
    } else if (key === 'saturday') {
      question = L.questionSaturday;
      hint = `Where would ${pp.they} rather be?`;
      body = tiles('sat', SATURDAY, 'radio', (v) => s.sat === v, 'grid');
    } else if (key === 'kind') {
      const opts = this.kindOptions(s);
      if (s.kind && !opts.some((o) => o.v === s.kind)) s.kind = '';
      question = L.questionKind;
      hint = 'Pick one.';
      body = tiles('kind', opts, 'radio', (v) => s.kind === v, 'grid');
    } else {
      const opts = this.bandOptions(s);
      if (s.band != null && !opts.some((o) => o.v === s.band)) s.band = null;
      question = L.questionBudget;
      hint = 'Per gift. Everything shown is in stock.';
      body = tiles('band', opts, 'radio', (v) => String(s.band) === String(v), 'grid');
    }
    const last = index === path.length - 1;
    const back = index > 0 ? `<button type="button" class="hc-gf__link" data-hc-gf="back">Back</button>` : '<span></span>';
    const nav = `<div class="hc-gf__nav">${back}<button type="submit" class="hc-gf__button" data-hc-gf-next ${this.answered(key) ? '' : 'disabled'}>${last ? 'Show gift ideas' : 'Next'}</button></div>`;
    this.paintStep({ question, hint, body: `${body}${nav}`, path, index });
  }

  /* The panel around a step: progress, question and hint, the answers, and
     beside the question the answers so far (each a way back) or, on the
     first step, what the results will hold. */
  paintStep({ question, hint, body, path, index }) {
    const L = this.config.labels;
    const meter = `<ol class="hc-gf__meter" style="--hc-gf-stops: ${path.length}" aria-hidden="true">${path
      .map((k, i) => `<li class="hc-gf__meter-step${i < index ? ' is-done' : ''}${i === index ? ' is-current' : ''}"><span>${STEP_LABELS[k] || k}</span></li>`)
      .join('')}</ol>`;
    const sofar = this.sofar(path, index);
    const aside = sofar.length
      ? `<div class="hc-gf__sofar"><p class="hc-gf__aside-title">So far</p><ul class="hc-gf__chips hc-gf__chips--sofar">${sofar
          .map((c) => `<li><button type="button" class="hc-gf__chip hc-gf__chip--button" data-hc-gf="goto" data-step="${esc(c.key)}" title="Change">${esc(c.text)}</button></li>`)
          .join('')}</ul></div>`
      : `<div class="hc-gf__sofar hc-gf__sofar--legend"><p class="hc-gf__aside-title">You'll get</p><ul class="hc-gf__legend">
          <li>Their gift personality<small>From the vibe and the perfect Saturday</small></li>
          <li>A top 3<small>The picks to wrap first</small></li>
          <li>More ideas<small>From our holiday collections</small></li>
          <li>${esc(L.stuffers || 'Stocking stuffers')}<small>Small things under ${money(this.config.stufferMax)}</small></li>
        </ul></div>`;
    this.stage.innerHTML = `<form class="hc-gf__panel" novalidate>
      <div class="hc-gf__progress"><p class="hc-gf__count">Question ${index + 1} of ${path.length}</p>${meter}</div>
      <div class="hc-gf__step">
        <div class="hc-gf__lead">
          <h3 class="hc-gf__question" id="${this.qid}" tabindex="-1">${esc(question)}</h3>
          <p class="hc-gf__hint">${esc(hint)}</p>
        </div>
        <div class="hc-gf__body">${body}</div>
        <div class="hc-gf__aside">${aside}</div>
      </div>
    </form>`;
    this.entered(this.stage.querySelector('.hc-gf__question'));
  }

  renderWaiting(key, path, index) {
    const L = this.config.labels;
    const tiles = Array.from({ length: 4 }, () => '<span class="hc-gf__tile hc-gf__skeleton"><span class="hc-gf__tile-face"></span></span>').join('');
    this.paintStep({
      question: { vibe: L.questionVibe, kind: L.questionKind, budget: L.questionBudget }[key] || '',
      hint: 'Loading the gift guide…',
      body: `<div class="hc-gf__tiles hc-gf__tiles--grid" aria-hidden="true">${tiles}</div>`,
      path,
      index,
    });
  }

  sofar(path, index) {
    const s = this.state;
    const out = [];
    const add = (key, text) => {
      const i = path.indexOf(key);
      if (i >= 0 && i < index && text) out.push({ key, text });
    };
    add('who', WHO.find((w) => w.v === s.who)?.label);
    add('gender', GENDER.find((g) => g.v === s.gender)?.label);
    add('vibe', VIBE[s.vibe]?.label);
    add('saturday', SATURDAY.find((x) => x.v === s.sat)?.label);
    add('kind', KINDS.find((k) => k.v === s.kind)?.label);
    return out;
  }

  /* Focus follows the step (WCAG 2.4.3), and a step that starts above the
     screen scrolls back into view. Not on first paint: the page should not
     jump to the quiz. */
  entered(target) {
    this.stage.setAttribute('data-hc-gf-enter', '');
    if (!this.moved) {
      this.moved = true;
      return;
    }
    if (this.stage.getBoundingClientRect().top < 0) {
      this.stage.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    target?.focus({ preventScroll: true });
  }

  onChange = (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement || t instanceof HTMLSelectElement)) return;
    if (t.closest('[data-hc-gf-card]')) return;
    const s = this.state;
    if (t.name === 'who') {
      if (!s.who) this.track('start', { who: t.value });
      if (s.who !== t.value) Object.assign(s, { gender: '', vibe: '', kind: '', band: null });
      s.who = t.value;
      this.load();
    } else if (t.name === 'gender') {
      if (s.gender !== t.value) Object.assign(s, { vibe: '', kind: '', band: null });
      s.gender = t.value;
    } else if (t.name === 'vibe') {
      s.vibe = t.value;
    } else if (t.name === 'sat') {
      s.sat = t.value;
    } else if (t.name === 'kind') {
      if (s.kind !== t.value) s.band = null;
      s.kind = t.value;
    } else if (t.name === 'band') {
      s.band = parseInt(t.value, 10) || 0;
    } else {
      return;
    }
    const next = this.stage.querySelector('[data-hc-gf-next]');
    if (next) {
      next.disabled = !this.answered(this.at);
      /* "Little extras" has no budget step, so the button says so. */
      const path = this.path();
      next.textContent = path.indexOf(this.at) === path.length - 1 ? 'Show gift ideas' : 'Next';
    }
  };

  answered(key) {
    const s = this.state;
    if (key === 'who') return !!s.who;
    if (key === 'gender') return !!s.gender;
    if (key === 'vibe') return !!s.vibe;
    if (key === 'saturday') return !!s.sat;
    if (key === 'kind') return !!s.kind;
    return s.band != null;
  }

  /* Enter on an answer is Next. No answer moves on by itself (WCAG 3.2.2):
     a tap selects, Next confirms. */
  onKey = (e) => {
    const t = e.target;
    if (e.key === 'Escape' && t.closest?.('.hc-gf__picker')) {
      this.closePicker(t.closest('[data-hc-gf-card]'));
      return;
    }
    if (e.key !== 'Enter' || !(t instanceof HTMLInputElement) || t.type !== 'radio' || t.closest('[data-hc-gf-card]')) return;
    e.preventDefault();
    if (!t.checked) {
      t.checked = true;
      t.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.next();
  };

  onSubmit = (e) => {
    e.preventDefault();
    this.next();
  };

  next() {
    const key = this.at;
    if (!this.answered(key)) return;
    const s = this.state;
    this.track('step', { step: key, who: s.who, gender: s.gender, vibe: s.vibe, saturday: s.sat, kind: s.kind, band: s.band ?? '' });
    const path = this.path();
    const i = path.indexOf(key);
    if (i < 0 || i + 1 >= path.length) return this.showResults();
    this.at = path[i + 1];
    this.renderStep();
  }

  onClick = (e) => {
    const btn = e.target.closest('[data-hc-gf]');
    if (!btn || !this.contains(btn)) return;
    const action = btn.dataset.hcGf;
    if (action === 'back') {
      const path = this.path();
      this.at = path[Math.max(0, path.indexOf(this.at) - 1)];
      this.renderStep();
    } else if (action === 'goto') {
      this.at = btn.dataset.step;
      this.renderStep();
    } else if (action === 'restart') {
      this.state = this.blank();
      this.at = 'who';
      this.results = null;
      this.syncUrl(true);
      this.renderStep();
    } else if (action === 'copy') {
      this.syncUrl(false);
      const url = location.href;
      (navigator.clipboard?.writeText(url) || Promise.reject()).then(
        () => this.toast('Link copied - send it to whoever is buying.'),
        () => this.toast(url)
      );
    } else if (action === 'shuffle') {
      this.state.deal = Math.min(99, (this.state.deal || 0) + 1);
      this.showResults();
    } else if (action === 'anyband') {
      this.state.band = 0;
      this.showResults();
    } else if (action === 'retry') {
      if (this.at === 'results') this.showResults();
      else this.renderStep();
    } else if (action === 'add') {
      this.onAdd(btn);
    } else if (action === 'pick') {
      this.onPick(btn);
    } else if (action === 'close-picker') {
      this.closePicker(btn.closest('[data-hc-gf-card]'));
    }
  };

  /* --- results ---------------------------------------------------------- */
  async showResults() {
    const s = this.state;
    this.at = 'results';
    this.renderSkeleton();
    const ok = await this.load();
    if (!ok) return this.renderError();
    if (!s.who || !s.vibe || !s.kind || (WHO.find((w) => w.v === s.who)?.ask && !s.gender)) {
      this.at = 'who';
      return this.renderStep();
    }
    if (s.band == null) s.band = 0;
    this.syncUrl(false);
    const run = (this.run = (this.run || 0) + 1);
    let res = null;
    let failed = false;
    try {
      res = await this.compose(s);
    } catch (e) {
      failed = true;
    }
    if (run !== this.run) return; // superseded by a newer answer set
    /* The store throttling the product reads is not "nothing fits". */
    if (failed) return this.renderError();
    this.results = res;
    this.renderResults();
    const count = res ? res.top.length + res.rows.reduce((n, r) => n + r.items.length, 0) : 0;
    this.track('results', { who: s.who, gender: s.gender, vibe: s.vibe, saturday: s.sat, kind: s.kind, band: s.band, count });
  }

  /* The score: the product's place in the collections that brought it (the
     store's own merchandising), the vibe (its collection counts most, then
     its type), the perfect Saturday, the collection made for this person,
     the person's own gift collection, staff picks, the season tag, price
     fit for the top 3, and season sense (no sandals in a December set). */
  score(it, s, role, band) {
    let pos = Infinity;
    for (const [h, v] of it.in) {
      const c = this.byHandle.get(h);
      if (!c || c.kind === 'price') continue;
      if (role === 'stuff' && c.kind !== 'stuffers' && !c.vibes.includes(s.vibe)) continue;
      if (v < pos) pos = v;
    }
    if (pos === Infinity) pos = Math.min(...it.in.values()) + 24;
    let sc = 1 / (1 + pos * (role === 'stuff' ? 0.03 : 0.05));
    const vh = it.vibes[s.vibe] || 0;
    sc += vh === 2 ? 0.9 : vh === 1 ? 0.35 : 0;
    if (s.sat && s.sat !== s.vibe) {
      const sh = it.vibes[s.sat] || 0;
      sc += sh === 2 ? 0.45 : sh === 1 ? 0.2 : 0;
    } else if (s.sat && vh) {
      sc += 0.2;
    }
    if (this.relFit(it, s)) sc += 0.5;
    const own = { m: 'men', w: 'women', k: 'kids' }[this.aud(s)];
    if (own && this.inKind(it, (c) => c.kind === own)) sc += 0.15;
    if (it.staff || this.inKind(it, (c) => c.kind === 'curated')) sc += s.vibe === 'everything' ? 0.4 : 0.25;
    const tag = this.config.season;
    if (tag && it.tags.some((t) => t.toLowerCase() === tag)) sc += 0.1;
    if (role === 'top' && band.hi !== Infinity && s.kind !== 'extras') {
      const mid = (band.lo + band.hi) / 2;
      const span = Math.max(band.hi - band.lo, 1);
      sc += 0.2 * (1 - Math.min(1, Math.abs(it.p - mid) / span));
    }
    const month = new Date().getMonth();
    const T = padded(it.ty);
    if ([10, 11, 0, 1].includes(month)) {
      if (/ SWIM | SANDALS | COVERUP | SUN\|BEACH | HATS SUN /.test(T)) sc -= 0.5;
      else if (/ SHORTS /.test(T)) sc -= 0.25;
    }
    if ([4, 5, 6, 7].includes(month) && / WINTER | THERMAL | INSULATED | SKI | GLOVES | SCARV/.test(T)) sc -= 0.4;
    if (it.tags.some((t) => /^low stock$/i.test(t))) sc -= 0.05;
    if (role === 'stuff' && UNIVERSAL.has(it.last)) sc -= 0.2;
    return sc;
  }

  /* The collection a product came from for these answers: its vibe
     collection, the one made for this person, then its first theme,
     curated or stuffers home. */
  sourceOf(it, s) {
    const order = [
      (c) => c.vibes.includes(s.vibe),
      (c) => c.rel && this.relFit(it, s) && c.rel === (s.who === 'teen' ? 'teen' : s.who),
      (c) => ['theme', 'curated', 'stuffers'].includes(c.kind),
    ];
    for (const test of order) {
      for (const h of it.in.keys()) {
        const c = this.byHandle.get(h);
        if (c && test(c)) return c;
      }
    }
    return this.byHandle.get(it.in.keys().next().value);
  }

  /* The reason line: a title word first (the type says "camp accessories",
     the product is a camera), then the product type's line, then where it
     came from. */
  reasonFor(it, s) {
    const kb = this.config.kb;
    const pp = this.people(s);
    let t = '';
    for (const [re, line] of kb.words || []) {
      if (new RegExp(re, 'i').test(it.t)) {
        t = line;
        break;
      }
    }
    if (!t) t = (kb.reasons || {})[it.last] || '';
    if (!t) {
      const c = this.sourceOf(it, s);
      t = c ? `From ${c.label}` : '';
    }
    t = this.pronouns(t, pp);
    if (it.staff) t = t ? `Staff pick · ${t}` : 'Staff pick';
    return t;
  }
  pronouns(t, pp) {
    return String(t)
      .replace(/\{they\}/g, pp.they)
      .replace(/\{They\}/g, pp.they[0].toUpperCase() + pp.they.slice(1))
      .replace(/\{them\}/g, pp.obj)
      .replace(/\{their\}/g, pp.pos);
  }

  /* Picks n from a dealt list, keeping a set varied: one of each product
     root, capped per brand and per type. With `hydrate`, candidates are
     read from /products/<handle>.js (up to four at a time, two for a single
     pick) and one that sold out since the page cached, whose in-stock
     options all cost more than the cap, or that is down to two sizes or
     fewer (minSizes) is skipped. Rows skip that read: the collection data
     already holds only products in stock. */
  async take(dealt, n, opts) {
    const { chosen, roots, vendors = new Map(), types = new Map(), vendorCap = 1, typeCap = 1, minSizes = 0, cap = Infinity, hydrate = false } = opts;
    const out = [];
    const fits = (it) => !chosen.has(it.h) && !roots.has(it.root) && (vendors.get(it.v) || 0) < vendorCap && (types.get(it.last) || 0) < typeCap;
    const keep = (it, p) => {
      out.push({ ...it, product: p || null, cap });
      chosen.add(it.h);
      roots.add(it.root);
      vendors.set(it.v, (vendors.get(it.v) || 0) + 1);
      types.set(it.last, (types.get(it.last) || 0) + 1);
    };
    let i = 0;
    if (!hydrate) {
      while (out.length < n && i < dealt.length) {
        const it = dealt[i++].it;
        if (fits(it)) keep(it, null);
      }
      return out;
    }
    while (out.length < n && i < dealt.length) {
      const batch = [];
      while (batch.length < Math.min(4, n - out.length + 1) && i < dealt.length) {
        const d = dealt[i++];
        if (fits(d.it)) batch.push(d.it);
      }
      if (!batch.length) break;
      const hydrated = await Promise.all(batch.map((it) => HcGiftFinder.productJson(it.h).then((p) => [it, p])));
      for (const [it, p] of hydrated) {
        if (out.length >= n) break;
        if (!p || !p.variants?.some((v) => v.available && v.price / 100 <= cap) || !fits(it)) continue;
        if (minSizes && this.sizeCount(p) < minSizes) continue;
        keep(it, p);
      }
    }
    return out;
  }

  sizeCount(p) {
    const si = this.optionNames(p).findIndex((n) => isSizeName(n));
    if (si < 0) return Infinity;
    return new Set(p.variants.filter((v) => v.available).map((v) => v[`option${si + 1}`])).size;
  }

  /* The vibe's own collection for this person (Cozy Comforts for her, Cozy
     Gifts for him), for the row title and its "See all". */
  vibeCollection(v, s) {
    const a = this.aud(s);
    return this.cols.find((c) => c.vibes.includes(v) && (!c.aud || c.aud === a)) || null;
  }

  /* Composition. Weighted sampling inside the quality tier
     (Efraimidis-Spirakis keys seeded by the answers and the "different
     ideas" count): a product a third of a point behind the leader still
     wins about a quarter of the deals, one a full point behind almost
     never, and a shared link always shows the same set. The rows deal
     looser than the top 3: variety matters more there. */
  async compose(s) {
    const cfg = this.config;
    const seed = this.encode(s);
    const band = this.bandOf(s);
    const pp = this.people(s);
    const chosen = new Set();
    const roots = new Set();
    const deal = (list, role) =>
      list
        .map((it) => {
          const sc = this.score(it, s, role, band);
          const u = ((hash(`${it.h}|${seed}|${role}`) % 99991) + 1) / 99992;
          return { it, key: Math.pow(u, 1 / Math.exp(sc / (role === 'top' ? 0.35 : 0.5))) };
        })
        .sort((a, b) => b.key - a.key);
    const base = this.shelf(s).filter((it) => this.kindFit(it, s.kind));
    const within = (lo, hi) => base.filter((it) => it.p >= lo && it.p < hi);
    let inBand = within(band.lo, band.hi);
    let over = false;
    if (inBand.length < 6 && band.hi !== Infinity && s.kind !== 'extras') {
      /* A thin band reaches a little over it (a $110 pick for a $50-100
         ask) rather than show a hole; those cards say so. */
      inBand = inBand.concat(within(band.hi, band.hi * 1.2));
      over = true;
    }
    const cap = over ? band.hi * 1.2 : band.hi;
    const failures = HcGiftFinder.failures;

    /* Top 3: the best of the band for the vibe, three brands and three
       kinds of thing. With no budget they should still feel like presents,
       so they come from the first band up when that holds enough. */
    let topList = inBand.filter((it) => it.vibes[s.vibe]);
    if (topList.length < 6) topList = inBand;
    if (!s.band && s.kind !== 'extras') {
      const floor = this.aud(s) === 'k' ? cfg.bands[0] / 2 : cfg.bands[0];
      const present = topList.filter((it) => it.p >= floor);
      if (present.length >= (this.aud(s) === 'k' ? 3 : 6)) topList = present;
    }
    const dealtTop = deal(topList, 'top');
    const tv = new Map();
    const tt = new Map();
    let top = await this.take(dealtTop, 3, { chosen, roots, vendors: tv, types: tt, cap, minSizes: 3, hydrate: true });
    if (top.length < 3) top = top.concat(await this.take(dealtTop, 3 - top.length, { chosen, roots, vendors: tv, types: tt, cap, hydrate: true }));
    /* Still short (a narrow shelf: "either", or a vibe without its own
       collection yet): the whole band, a second piece per brand and kind. */
    if (top.length < 3) top = top.concat(await this.take(deal(inBand, 'top'), 3 - top.length, { chosen, roots, vendors: tv, types: tt, cap, vendorCap: 2, typeCap: 2, hydrate: true }));
    if (!top.length) {
      if (HcGiftFinder.failures > failures) throw new Error('product reads failed');
      return { top: [], rows: [] };
    }

    const rows = [];
    const rail = cfg.rail;
    const pickRow = async (list, role, caps = {}) => {
      const dealt = deal(list, role);
      const rv = new Map();
      const rt = new Map();
      let got = await this.take(dealt, rail, { chosen, roots, vendors: rv, types: rt, vendorCap: 2, typeCap: 2, cap, ...caps });
      if (got.length < rail) got = got.concat(await this.take(dealt, rail - got.length, { chosen, roots, vendors: rv, types: rt, vendorCap: 3, typeCap: 3, cap, ...caps }));
      return got;
    };
    /* Row 1: more for the vibe, from its own collection when there is one. */
    const vc = this.vibeCollection(s.vibe, s);
    const vibeRow = await pickRow(inBand.filter((it) => it.vibes[s.vibe]), 'more');
    if (vibeRow.length >= 3) rows.push({ key: 'vibe', title: vc ? `More from ${vc.label}` : `More for ${pp.obj}`, items: vibeRow, col: vc });
    /* Row 2: their other side, from the perfect Saturday. */
    if (s.sat && s.sat !== s.vibe) {
      const sc = this.vibeCollection(s.sat, s);
      let satList = inBand.filter((it) => it.vibes[s.sat]);
      /* Too narrow inside the chosen kind of gift: their other side may be
         a thing to use as well as to wear. */
      if (satList.length < 6) satList = this.shelf(s).filter((it) => it.vibes[s.sat] && it.p >= band.lo && it.p < cap);
      const satRow = await pickRow(satList, 'more');
      if (satRow.length >= 3) rows.push({ key: 'sat', title: `For ${pp.pos} ${VIBE[s.sat].side}`, items: satRow, col: sc });
    }
    /* Row 3: stocking stuffers under the ceiling (the vibe's first). */
    if (s.kind !== 'extras') {
      const list = this.shelf(s).filter((it) => it.p <= cfg.stufferMax);
      const stuff = await pickRow(list, 'stuff', { cap: cfg.stufferMax, vendorCap: 1, typeCap: 1 });
      if (stuff.length >= 3) rows.push({ key: 'stuff', title: `${cfg.labels.stuffers || 'Stocking stuffers'} under ${money(cfg.stufferMax)}`, items: stuff, col: this.byHandle.get(this.stuffHandles[0]) || null });
    }
    /* Never a near-empty page: when the vibe rows came up short, fill with
       the rest of the answers. */
    if (rows.reduce((n, r) => n + r.items.length, 0) < rail) {
      const fill = await pickRow(inBand, 'more');
      if (fill.length >= 3) rows.unshift({ key: 'more', title: `More for ${pp.obj}`, items: fill, col: null });
    }
    const mark = (list) => list.map((it) => Object.assign(it, { over: over && it.p >= band.hi, reason: this.reasonFor(it, s), src: this.sourceOf(it, s) }));
    mark(top);
    rows.forEach((r) => mark(r.items));
    return { top, rows };
  }

  renderSkeleton() {
    const cards = Array.from({ length: 3 }, () => `<div class="hc-gf__card hc-gf__skeleton"><span class="hc-gf__media"></span><span class="hc-gf__skeleton-line"></span><span class="hc-gf__skeleton-line hc-gf__skeleton-line--short"></span></div>`).join('');
    this.stage.innerHTML = `<div class="hc-gf__results">
      <p class="hc-gf__status" role="status">Working out their gift personality…</p>
      <div class="hc-gf__grid hc-gf__grid--top" aria-hidden="true">${cards}</div>
    </div>`;
  }

  renderError() {
    const L = this.config.labels;
    const guide = this.config.shopAll ? ` <a class="hc-gf__link" href="${esc(this.config.shopAll)}">${esc(L.shopAllLabel)}</a>` : '';
    this.stage.innerHTML = `<div class="hc-gf__empty" role="alert">
      <p>The gift guide didn't load. Check your connection and try again.</p>
      <button type="button" class="hc-gf__button" data-hc-gf="retry">Try again</button>${guide}
    </div>`;
  }

  /* The answers read back as chips; each is a way back to its question. */
  answerChips() {
    const s = this.state;
    const path = this.path();
    const out = [
      { key: 'who', text: WHO.find((w) => w.v === s.who)?.label },
      { key: 'gender', text: GENDER.find((g) => g.v === s.gender)?.label },
      { key: 'vibe', text: VIBE[s.vibe]?.label },
      { key: 'saturday', text: SATURDAY.find((x) => x.v === s.sat)?.label },
      { key: 'kind', text: KINDS.find((k) => k.v === s.kind)?.label },
      { key: 'budget', text: this.bandLabel(s.band) },
    ];
    return out.filter((c) => c.text && path.includes(c.key));
  }

  /* "She's a cozy homebody with an adventurous side." */
  personality() {
    const s = this.state;
    const pp = this.people();
    const v = VIBE[s.vibe];
    const side = s.sat && s.sat !== s.vibe ? VIBE[s.sat]?.side : '';
    return { head: `${pp.is} ${v.name}`, side: side ? `with ${/^[aeiou]/.test(side) ? 'an' : 'a'} ${side}` : '', line: this.pronouns(v.line, pp) };
  }

  giftCardBox() {
    const gc = this.giftCard;
    const note = this.config.labels.giftCardNote;
    if (!gc || !note) return '';
    return `<aside class="hc-gf__giftcard" aria-label="${esc(gc.label)}">
      ${gc.img ? `<span class="hc-gf__giftcard-media"><img src="${esc(sized(gc.img, 240))}" alt="" loading="lazy" decoding="async" width="120" height="76"></span>` : ''}
      <p class="hc-gf__giftcard-note">${esc(note)}</p>
      <a class="hc-gf__button hc-gf__button--quiet hc-gf__button--small" href="${esc(gc.url)}">Shop gift cards</a>
    </aside>`;
  }

  renderResults() {
    const L = this.config.labels;
    const r = this.results;
    const pp = this.people();
    this.cards.clear();
    const who = this.personality();
    const linkIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7"/></svg>';
    const art = r && r.top.length ? `<div class="hc-gf__hero-art" aria-hidden="true">${r.top.map((it) => `<span><img src="${esc(sized(it.img, 360))}" alt="" decoding="async" width="180" height="180"></span>`).join('')}</div>` : '';
    const head = `<div class="hc-gf__hero">
        <div class="hc-gf__hero-text">
          <h3 class="hc-gf__results-title" tabindex="-1">${esc(who.head)}${who.side ? ` <span class="hc-gf__results-side">${esc(who.side)}</span>` : ''}</h3>
          <p class="hc-gf__results-line">${esc(who.line)}</p>
          <ul class="hc-gf__chips" aria-label="Your answers">${this.answerChips()
            .map((c) => `<li><button type="button" class="hc-gf__chip hc-gf__chip--button" data-hc-gf="goto" data-step="${c.key}" title="Change">${esc(c.text)}</button></li>`)
            .join('')}</ul>
          <div class="hc-gf__actions">
            <button type="button" class="hc-gf__button hc-gf__button--small" data-hc-gf="copy">${linkIcon}<span>Copy link</span></button>
            <button type="button" class="hc-gf__button hc-gf__button--quiet hc-gf__button--small" data-hc-gf="shuffle">Different ideas</button>
            <button type="button" class="hc-gf__button hc-gf__button--quiet hc-gf__button--small" data-hc-gf="restart">Start over</button>
          </div>
        </div>
        ${art}
      </div>`;
    if (!r || !r.top.length) {
      const any = this.state.band ? `<button type="button" class="hc-gf__button" data-hc-gf="anyband">Try any budget</button> ` : '';
      this.stage.innerHTML = `<div class="hc-gf__results" role="region" aria-label="${esc(L.results)}">${head}
        <div class="hc-gf__empty" role="status">
          <p>Nothing in the gift guide fits all of that right now.</p>
          ${any}<button type="button" class="hc-gf__button hc-gf__button--quiet" data-hc-gf="goto" data-step="kind">Change the answers</button>
        </div>${this.giftCardBox()}</div>`;
      this.entered(this.stage.querySelector('.hc-gf__results-title'));
      return;
    }
    const seeAll = (col) => (col ? `<a class="hc-gf__link hc-gf__see-all" href="${ROOT}collections/${esc(col.handle)}">See all</a>` : '');
    const topTitle = r.top.length > 1 ? `Top ${r.top.length} for ${pp.obj}` : `Our top pick for ${pp.obj}`;
    const top = `<section class="hc-gf__group hc-gf__group--top" aria-label="${esc(topTitle)}">
        <div class="hc-gf__group-head"><h4 class="hc-gf__group-title">${esc(topTitle)}</h4></div>
        <div class="hc-gf__grid hc-gf__grid--top">${r.top.map((it) => this.renderCard(it, true)).join('')}</div>
      </section>`;
    const rows = r.rows
      .map(
        (row) => `<section class="hc-gf__group" aria-label="${esc(row.title)}">
        <div class="hc-gf__group-head"><h4 class="hc-gf__group-title">${esc(row.title)}</h4>${seeAll(row.col)}</div>
        <div class="hc-gf__grid hc-gf__grid--row">${row.items.map((it) => this.renderCard(it, false)).join('')}</div>
      </section>`
      )
      .join('');
    const count = r.top.length + r.rows.reduce((n, x) => n + x.items.length, 0);
    this.stage.innerHTML = `<div class="hc-gf__results" role="region" aria-label="${esc(L.results)}">${head}
      <p class="visually-hidden" role="status">${count} gift ideas, all in stock.</p>
      ${top}${rows}${this.giftCardBox()}</div>`;
    this.entered(this.stage.querySelector('.hc-gf__results-title'));
  }

  /* A card in the store's own anatomy: square photo, brand, name, price,
     one round Add button on the photo. The top 3 also carry their reason. */
  renderCard(item, lead) {
    const id = `hc-gf-${item.h}`;
    this.cards.set(id, { item });
    const url = `${ROOT}products/${item.h}`;
    const sale = item.cp > item.p;
    const from = !sale && item.pm > item.p + 0.001;
    const price = `${from ? 'From ' : ''}${money(item.p)}${sale ? ` <s>${money(item.cp)}</s>` : ''}`;
    const why = item.over ? `A little over budget · ${item.reason}` : item.reason;
    return `<article class="hc-gf__card${lead ? ' hc-gf__card--lead' : ''}" data-hc-gf-card="${id}">
      <div class="hc-gf__media">
        <a href="${esc(url)}" tabindex="-1" aria-hidden="true"><img src="${esc(sized(item.img, lead ? 720 : 480))}" alt="" loading="lazy" decoding="async" width="360" height="360"></a>
        <button type="button" class="hc-gf__plus" data-hc-gf="add" aria-label="Add ${esc(item.t)} to cart">${icon('plus', 'hc-gf__plus-icon')}</button>
      </div>
      <div class="hc-gf__card-text">
        ${item.v ? `<p class="hc-gf__vendor">${esc(item.v)}</p>` : ''}
        <h5 class="hc-gf__name"><a href="${esc(url)}">${esc(item.t)}</a></h5>
        <p class="hc-gf__price${sale ? ' hc-gf__price--sale' : ''}">${price}</p>
        ${lead && why ? `<p class="hc-gf__why">${esc(why)}</p>` : ''}
      </div>
    </article>`;
  }

  optionNames(p) {
    return (p.options || []).map((o) => (typeof o === 'string' ? o : o.name));
  }
  colourIndex(p) {
    return this.optionNames(p).findIndex((n) => isColourName(n));
  }
  /* Values for an option, scoped the way the PDP and the cart upsell do:
     colours list every colour with an available variant (minus colours with
     no image); other options list only values available within the chosen
     colour. */
  valuesFor(p, sel, optIndex) {
    const names = this.optionNames(p);
    const key = `option${optIndex + 1}`;
    const isColour = isColourName(names[optIndex]);
    const ci = this.colourIndex(p);
    const anyMedia = p.variants.some((v) => v.featured_image);
    const scope = !isColour && ci >= 0 && sel ? sel[names[ci]] : null;
    const seen = [];
    for (const v of p.variants) {
      const val = v[key];
      if (val == null || seen.includes(val)) continue;
      let run = p.variants.filter((x) => x[key] === val);
      if (scope != null) run = run.filter((x) => x[`option${ci + 1}`] === scope);
      if (!run.some((x) => x.available)) continue;
      if (isColour && anyMedia && !run.some((x) => x.featured_image)) continue;
      seen.push(val);
    }
    return seen;
  }
  resolveVariant(p, sel) {
    const names = this.optionNames(p);
    return p.variants.find((v) => names.every((n, i) => v[`option${i + 1}`] === sel[n]) && v.available) || null;
  }
  /* The option a card opens on: in stock, inside the budget the card was
     picked for, with a photo. */
  defaultSelection(p, cap = Infinity) {
    const names = this.optionNames(p);
    const ci = this.colourIndex(p);
    const fits = (x) => x.available && x.price / 100 <= cap;
    const v =
      p.variants.find((x) => fits(x) && (ci < 0 || x.featured_image)) ||
      p.variants.find(fits) ||
      p.variants.find((x) => x.available && (ci < 0 || x.featured_image)) ||
      p.variants.find((x) => x.available) ||
      p.variants[0];
    const sel = {};
    names.forEach((n, i) => (sel[n] = v[`option${i + 1}`]));
    return sel;
  }

  /* Add: one option in stock goes straight to the cart; otherwise a small
     picker opens on the card with the options that are in stock (colour
     first, then size; the last choice adds). */
  async onAdd(btn) {
    const card = btn.closest('[data-hc-gf-card]');
    const entry = this.cards.get(card?.dataset.hcGfCard);
    if (!entry || btn.dataset.state === 'busy') return;
    btn.dataset.state = 'busy';
    const p = entry.item.product || (await HcGiftFinder.productJson(entry.item.h));
    btn.dataset.state = '';
    if (!p || !p.variants?.some((v) => v.available)) return this.toast('That one just sold out - try another.');
    entry.item.product = p;
    const live = p.variants.filter((v) => v.available);
    const names = this.optionNames(p);
    const real = names.filter((n, i) => this.valuesFor(p, this.defaultSelection(p, entry.item.cap ?? Infinity), i).length > 1);
    if (live.length === 1 || !real.length) {
      const sel = this.defaultSelection(p, entry.item.cap ?? Infinity);
      return this.addVariant(card, entry, this.resolveVariant(p, sel) || live[0]);
    }
    entry.sel = this.defaultSelection(p, entry.item.cap ?? Infinity);
    this.openPicker(card, entry);
  }

  openPicker(card, entry) {
    this.querySelectorAll('.hc-gf__picker').forEach((x) => {
      x.closest('[data-hc-gf-card]')?.classList.remove('is-picking');
      x.remove();
    });
    const p = entry.item.product;
    const names = this.optionNames(p);
    const choices = names.map((n, i) => i).filter((i) => this.valuesFor(p, entry.sel, i).length > 1);
    const lastRow = choices[choices.length - 1];
    /* Earlier rows (the colour) show the current choice; the last row (the
       size) starts unchosen, so a tap reads as the choice it is. */
    const rows = choices
      .map((i) => {
        const n = names[i];
        return `<div class="hc-gf__picker-row"><p class="hc-gf__picker-label">${esc(n)}</p><div class="hc-gf__picker-chips">${this.valuesFor(p, entry.sel, i)
          .map((v) => {
            const on = i !== lastRow && entry.sel[n] === v;
            return `<button type="button" class="hc-gf__picker-chip${on ? ' is-on' : ''}" data-hc-gf="pick" data-opt="${i}" data-value="${esc(v)}" aria-pressed="${on}">${esc(v)}</button>`;
          })
          .join('')}</div></div>`;
      })
      .join('');
    const picker = document.createElement('div');
    picker.className = 'hc-gf__picker';
    picker.setAttribute('role', 'group');
    picker.setAttribute('aria-label', `Choose options for ${entry.item.t}`);
    picker.innerHTML = `<button type="button" class="hc-gf__picker-close" data-hc-gf="close-picker" aria-label="Close">${icon('close', 'hc-gf__picker-x')}</button>${rows}`;
    card.querySelector('.hc-gf__media').appendChild(picker);
    card.classList.add('is-picking');
    const focus = this.pickFocus != null ? picker.querySelectorAll(`[data-opt="${this.pickFocus}"]`) : [];
    (picker.querySelector(`[data-opt="${this.pickFocus}"].is-on`) || focus[0] || picker.querySelector('.hc-gf__picker-chip'))?.focus({ preventScroll: true });
    this.pickFocus = null;
  }

  /* A chip sets its option. A choice in the last row (the size) adds the
     variant; an earlier one (the colour) re-scopes the rows after it. */
  onPick(btn) {
    const card = btn.closest('[data-hc-gf-card]');
    const entry = this.cards.get(card?.dataset.hcGfCard);
    const p = entry?.item.product;
    if (!p) return;
    const names = this.optionNames(p);
    const idx = parseInt(btn.dataset.opt, 10);
    entry.sel[names[idx]] = btn.dataset.value;
    let variant = this.resolveVariant(p, entry.sel);
    if (!variant) {
      /* Keep the clicked value, re-align the rest to something in stock. */
      const best = p.variants.find((v) => v.available && v[`option${idx + 1}`] === btn.dataset.value);
      if (best) names.forEach((n, i) => (entry.sel[n] = best[`option${i + 1}`]));
      variant = this.resolveVariant(p, entry.sel);
    }
    const rows = names.map((n, i) => i).filter((i) => this.valuesFor(p, entry.sel, i).length > 1);
    if (variant && idx === rows[rows.length - 1]) {
      this.closePicker(card);
      return this.addVariant(card, entry, variant);
    }
    this.pickFocus = idx;
    this.openPicker(card, entry);
  }

  closePicker(card) {
    card?.querySelector('.hc-gf__picker')?.remove();
    card?.classList.remove('is-picking');
    card?.querySelector('.hc-gf__plus')?.focus({ preventScroll: true });
  }

  addVariant(card, entry, variant) {
    const btn = card.querySelector('.hc-gf__plus');
    if (!variant || !btn || btn.dataset.state === 'busy') return;
    const src = entry.item.src?.handle || 'guide';
    btn.dataset.state = 'busy';
    btn.disabled = true;
    const body = new FormData();
    body.append('id', variant.id);
    body.append('quantity', '1');
    body.append('properties[_hc_gift]', `${this.state.who}-${src}`);
    fetch(`${ROOT}cart/add.js`, { method: 'POST', body, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } })
      .then((r) => r.json())
      .then((added) => {
        if (added && added.status) throw new Error(added.description || 'Could not add to cart');
        return fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      })
      .then((cart) => {
        const deferred = CartLinesUpdateEvent.createPromise();
        document.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'gift-finder', lines: [], promise: deferred.promise }));
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: false, items: cart.items, source: 'hc-gift-finder', itemCount: cart.item_count, productId: entry.item.product.id },
        });
        this.track('add', { who: this.state.who, collection: src, handle: entry.item.h, price: variant.price / 100 });
        btn.dataset.state = 'added';
        btn.innerHTML = icon('check', 'hc-gf__plus-icon');
        window.setTimeout(() => {
          if (!btn.isConnected) return;
          btn.dataset.state = '';
          btn.disabled = false;
          btn.innerHTML = icon('plus', 'hc-gf__plus-icon');
        }, 2200);
      })
      .catch(() => {
        btn.dataset.state = '';
        btn.disabled = false;
        this.toast('That one could not be added - it may have just sold out.');
      });
  }

  toast(text) {
    this.querySelector('.hc-gf__toast')?.remove();
    const t = document.createElement('div');
    t.className = 'hc-gf__toast';
    t.setAttribute('role', 'status');
    t.textContent = text;
    this.appendChild(t);
    window.setTimeout(() => t.remove(), 3200);
  }

  /* --- data ------------------------------------------------------------- */
  /* One collection, read through the hc-gift-quiz JSON view (250 a page,
     sold-out filtered by the storefront). Cached as a promise so two
     quizzes on a page share it; sessionStorage keeps it warm for 10 minutes.
     null = could not be read (unpublished, or the store did not answer;
     never cached); an empty list = empty. */
  static collection(handle) {
    if (HcGiftFinder.colCache.has(handle)) return HcGiftFinder.colCache.get(handle);
    const promise = (async () => {
      const ssKey = `hc:gq2:${handle}`;
      try {
        const kept = JSON.parse(sessionStorage.getItem(ssKey));
        if (kept && Date.now() - kept.t < 600000) return kept.r;
      } catch (e) {
        /* storage unavailable */
      }
      const url = `${ROOT}collections/${encodeURIComponent(handle)}?view=hc-gift-quiz&filter.v.availability=1`;
      const first = await HcGiftFinder.read(url);
      if (!first) {
        HcGiftFinder.colCache.delete(handle);
        return null;
      }
      const r = { handle: first.handle || handle, title: first.title || '', image: first.image || '', products: Array.isArray(first.products) ? first.products : [] };
      /* A collection past 250 in stock (rare in a gift guide) reads on. */
      for (let page = 2; page <= Math.min(Number(first.pages) || 1, 4); page++) {
        const more = await HcGiftFinder.read(`${url}&page=${page}`);
        if (more && Array.isArray(more.products)) r.products = r.products.concat(more.products);
      }
      try {
        sessionStorage.setItem(ssKey, JSON.stringify({ t: Date.now(), r }));
      } catch (e) {
        /* fine */
      }
      return r;
    })();
    HcGiftFinder.colCache.set(handle, promise);
    return promise;
  }

  /* The storefront rate-limits bursts (429, an HTML error page) and once
     stalled a request for ~30 s: each attempt is capped at 10 s, and a
     throttle or timeout is retried once after a short pause. An unpublished
     collection answers 404 and is skipped at once. */
  static async read(url) {
    for (let attempt = 0; attempt < 2; attempt++) {
      const ctl = new AbortController();
      const timer = window.setTimeout(() => ctl.abort(), 10000);
      try {
        const r = await fetch(url, { credentials: 'same-origin', signal: ctl.signal });
        if (r.ok) {
          const t = await r.text();
          if (t.trim().startsWith('{')) return JSON.parse(t);
        }
        if (r.status !== 429 && r.status < 500) return null;
      } catch (e) {
        /* network or timeout */
      } finally {
        window.clearTimeout(timer);
      }
      await new Promise((res) => window.setTimeout(res, 700 + attempt * 600));
    }
    return null;
  }

  /* A product's live options. A 404 (gone since the guide was read) is a
     plain null; a throttle, error or 8 s stall is retried once and then
     counted in `failures`, so the results can say the store did not answer
     instead of "nothing fits". Failures are never cached. */
  static productJson(handle) {
    if (!handle) return Promise.resolve(null);
    if (HcGiftFinder.productCache.has(handle)) return HcGiftFinder.productCache.get(handle);
    const promise = (async () => {
      for (let attempt = 0; attempt < 2; attempt++) {
        const ctl = new AbortController();
        const timer = window.setTimeout(() => ctl.abort(), 8000);
        try {
          const r = await fetch(`${ROOT}products/${handle}.js`, { headers: { Accept: 'application/json' }, signal: ctl.signal });
          if (r.ok) return await r.json();
          if (r.status === 404) return null;
        } catch (e) {
          /* network, timeout or an HTML error page */
        } finally {
          window.clearTimeout(timer);
        }
        await new Promise((res) => window.setTimeout(res, 800 + attempt * 800));
      }
      HcGiftFinder.failures += 1;
      HcGiftFinder.productCache.delete(handle);
      return null;
    })();
    HcGiftFinder.productCache.set(handle, promise);
    return promise;
  }
}

HcGiftFinder.colCache = new Map();
HcGiftFinder.productCache = new Map();
HcGiftFinder.failures = 0;

if (!customElements.get('hc-gift-finder')) {
  customElements.define('hc-gift-finder', HcGiftFinder);
}
